const BUGSNAG_REPORTED_ERROR = Symbol('bugsnagReportedError')
const BUGSNAG_LAST_ERROR_FINGERPRINT = 'bugsnag:lastReportedFingerprint'

type BugsnagMetadata = Record<string, unknown>

function markErrorAsReported(error: unknown): void {
  if (!error || typeof error !== 'object') {
    return
  }

  try {
    Object.defineProperty(error, BUGSNAG_REPORTED_ERROR, {
      configurable: true,
      enumerable: false,
      value: true,
    })
  }
  catch {
    // Ignore read-only error objects.
  }
}

function hasErrorAlreadyBeenReported(error: unknown): boolean {
  return Boolean(error && typeof error === 'object' && (error as Record<PropertyKey, unknown>)[BUGSNAG_REPORTED_ERROR])
}

function toReportableError(error: unknown): Error {
  if (error instanceof Error) {
    return error
  }

  if (typeof error === 'string') {
    return new Error(error)
  }

  return new Error('Une erreur inattendue est survenue')
}

function getErrorFingerprint(error: unknown): string {
  const reportableError = toReportableError(error)

  return [reportableError.name, reportableError.message, reportableError.stack ?? ''].join('|')
}

export function useBugsnagReporter() {
  const runtimeConfig = useRuntimeConfig()
  const bugsnag = useBugsnag()
  const userStore = useUserStore()
  const lastReportedFingerprint = useState<string | null>(BUGSNAG_LAST_ERROR_FINGERPRINT, () => null)

  const buildId = runtimeConfig.app.buildId
  const appVersion = runtimeConfig.public.appVersion || buildId

  const notifyError = (error: unknown, metadata: BugsnagMetadata = {}) => {
    const fingerprint = getErrorFingerprint(error)

    if (hasErrorAlreadyBeenReported(error) || lastReportedFingerprint.value === fingerprint) {
      return
    }

    lastReportedFingerprint.value = fingerprint
    markErrorAsReported(error)

    bugsnag.notify(toReportableError(error), (event: any) => {
      event.addMetadata('nuxt', {
        appVersion,
        buildId,
        ...metadata,
      })
    })
  }

  const syncUser = () => {
    const client = bugsnag as Record<string, any>

    if (!userStore.isLoggedIn || !userStore.userId) {
      if (typeof client.clearUser === 'function') {
        client.clearUser()
      }

      return
    }

    const fullName = userStore.fullName || [userStore.firstName, userStore.lastName].filter(Boolean).join(' ') || undefined

    if (typeof client.setUser === 'function') {
      client.setUser(String(userStore.userId), userStore.email ?? undefined, fullName)
    }
  }

  return {
    appVersion,
    buildId,
    notifyError,
    syncUser,
  }
}
